import { PLAN_PHASE, PLAN_PHASE_LABELS, formatSets, type TrainingDiagram, type TrainingExerciseDto, type TrainingPlanDto } from '@ukinfo/shared';
import { itemExerciseId } from './training';

/**
 * Skizzen und Einheiten als Bild (PNG) - zum Teilen im Messenger oder zum Herunterladen.
 *
 * Alles passiert im Browser und ohne Netz: Die Skizze ist ein SVG ohne fremde Ressourcen
 * (Pfeilspitzen als `<marker>` im eigenen `<defs>`, keine Webschrift), es wird als `data:`-Adresse
 * in ein Bild geladen und auf eine Leinwand gezeichnet. Eine Leinwand, in die nur Bilder
 * derselben Art gezeichnet wurden, bleibt "sauber" und laesst sich als PNG auslesen.
 *
 * Die Zeichnung der Skizze selbst (React, `PitchDiagram`) steckt in `components/training/sketchSvg`;
 * hier werden fertige SVG-Texte verarbeitet. Das haelt diese Datei frei von Oberflaeche und testbar.
 */

/** Ein serialisiertes SVG samt Seitenverhaeltnis. */
export interface SvgPicture {
  svg: string;
  width: number;
  height: number;
}

/** Zeichnet eine Skizze als SVG-Text (siehe `sketchPicture`). */
export type SketchRenderer = (diagram: TrainingDiagram, title?: string) => SvgPicture;

/** Schrift der Bilder: Systemschrift, nichts wird nachgeladen. */
export const IMAGE_FONT = '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/**
 * Name unten auf dem Bild: der Verein, sonst "UK Info". Die Oberfläche setzt ihn, sobald sie den Verein
 * kennt (AppShell); so brauchen die Knöpfe an den Skizzen keine eigene Abfrage.
 */
let imageBrand = 'UK Info';
export const setImageBrand = (name: string | null | undefined): void => { imageBrand = name?.trim() || 'UK Info'; };
export const getImageBrand = (): string => imageBrand;

/* ------------------------------------------------------------------ Hilfen */

/** Dateiname aus einem Titel: ohne Sonderzeichen und Pfadtrenner, nie leer. */
export function safeFileName(title: string, extension = 'png'): string {
  const base = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  return `${base || 'uk-info'}.${extension}`;
}

/** Laedt ein SVG als Bild. `data:` statt `blob:` - so bleibt die Leinwand in allen Browsern auslesbar. */
export function loadSvgImage(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Die Skizze ließ sich nicht in ein Bild umwandeln'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Das Bild ließ sich nicht erzeugen'))), 'image/png');
  });
}

type Context = CanvasRenderingContext2D;

/** Bricht Text auf die Breite um; mehr als `maxLines` Zeilen werden mit "…" gekuerzt. */
export function wrapLines(ctx: { measureText: (text: string) => { width: number } }, text: string, maxWidth: number, maxLines: number): string[] {
  const words = text.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const lines: string[] = [];
  let line = '';
  const fits = (value: string): boolean => ctx.measureText(value).width <= maxWidth;
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (fits(next) || !line) {
      line = next;
      continue;
    }
    lines.push(line);
    line = word;
  }
  if (line) lines.push(line);
  // Ein einzelnes Wort, das allein nicht passt, wird gekuerzt - sonst liefe es aus dem Bild.
  const clip = (value: string): string => {
    if (fits(value)) return value;
    let cut = value;
    while (cut.length > 1 && !fits(`${cut}…`)) cut = cut.slice(0, -1);
    return `${cut.trimEnd()}…`;
  };
  if (lines.length <= maxLines) return lines.map(clip);
  const kept = lines.slice(0, maxLines).map(clip);
  // Es gibt mehr Zeilen, als Platz ist: die letzte endet auf "…".
  kept[maxLines - 1] = clip(`${(kept[maxLines - 1] ?? '').replace(/…$/, '')}…`);
  return kept;
}

function roundedBox(ctx: Context, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function newCanvas(width: number, height: number): { canvas: HTMLCanvasElement; ctx: Context } {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width);
  canvas.height = Math.round(height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Dieser Browser kann kein Bild zeichnen');
  return { canvas, ctx };
}

/** Zum Messen von Text, bevor die Hoehe des Bildes feststeht. */
function measuring(): Context {
  return newCanvas(1, 1).ctx;
}

/* -------------------------------------------------------------- Eine Skizze */

const SKETCH_WIDTH = 600;
const SKETCH_PAD = 24;
const SKETCH_SCALE = 2;

/**
 * Eine Skizze als PNG: weisser Rand, darunter der Titel der Uebung, unten klein der Vereinsname.
 * Logische Breite 600 Punkte, doppelt aufgeloest (1200 Pixel).
 */
export async function makeSketchImage(picture: SvgPicture, title: string, brand: string): Promise<Blob> {
  const image = await loadSvgImage(picture.svg);
  const inner = SKETCH_WIDTH - 2 * SKETCH_PAD;
  const sketchHeight = (inner * picture.height) / picture.width;

  const titleFont = `700 26px ${IMAGE_FONT}`;
  const measure = measuring();
  measure.font = titleFont;
  const titleLines = title.trim() ? wrapLines(measure, title.trim(), inner, 2) : [];
  const titleHeight = titleLines.length * 34;
  const height = SKETCH_PAD + sketchHeight + (titleHeight ? 18 + titleHeight : 0) + 14 + 22 + 12;

  const { canvas, ctx } = newCanvas(SKETCH_WIDTH * SKETCH_SCALE, height * SKETCH_SCALE);
  ctx.scale(SKETCH_SCALE, SKETCH_SCALE);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SKETCH_WIDTH, height);
  ctx.drawImage(image, SKETCH_PAD, SKETCH_PAD, inner, sketchHeight);

  let y = SKETCH_PAD + sketchHeight + 18;
  ctx.fillStyle = '#0f172a';
  ctx.font = titleFont;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  for (const line of titleLines) {
    ctx.fillText(line, SKETCH_PAD, y);
    y += 34;
  }
  ctx.fillStyle = '#94a3b8';
  ctx.font = `500 15px ${IMAGE_FONT}`;
  ctx.textAlign = 'right';
  ctx.fillText(brand, SKETCH_WIDTH - SKETCH_PAD, height - 12 - 18);
  return canvasToBlob(canvas);
}

/* -------------------------------------------------------------- Eine Einheit */

export interface SessionImageItem {
  number: number;
  title: string;
  minutes: number;
  /** Durchgaenge der Uebung ("4 × 3 Min., 1 Min. Pause"), sonst null. */
  sets: string | null;
  note: string | null;
  /** Die wichtigsten Coaching-Punkte, gekuerzt. */
  coaching: string[];
  picture: SvgPicture | null;
}

export interface SessionImagePhase {
  label: string;
  minutes: number;
  items: SessionImageItem[];
}

export interface SessionImageData {
  title: string;
  /** Mannschaft, Datum und Ort. */
  subtitle: string;
  focus: string;
  totalMinutes: number;
  brand: string;
  phases: SessionImagePhase[];
}

/** Coaching-Text in kurze Punkte: Aufzaehlungszeichen weg, bei Fliesstext nach Saetzen, gekuerzt. */
export function coachingPoints(text: string | undefined | null, maxPoints = 3, maxChars = 110): string[] {
  if (!text?.trim()) return [];
  let parts = text.split(/\r?\n+/);
  if (parts.length < 2) parts = text.split(/(?<=[.!?])\s+/);
  return parts
    .map((part) => part.replace(/^[\s\-–•*·]+|^\d+[.)]\s+/, '').trim())
    .filter(Boolean)
    .slice(0, maxPoints)
    .map((part) => (part.length > maxChars ? `${part.slice(0, maxChars - 1).trimEnd()}…` : part));
}

/** Macht aus der Einheit die Angaben fuer das Bild. `exercises` darf Luecken haben - dann fehlen dort die Coaching-Punkte. */
export function buildSessionImageData(
  plan: TrainingPlanDto,
  exercises: Record<string, TrainingExerciseDto | undefined>,
  options: { title?: string; subtitle: string; brand: string; renderSketch: SketchRenderer },
): SessionImageData {
  const phases: SessionImagePhase[] = [];
  for (const phase of PLAN_PHASE) {
    const items = plan.items.filter((item) => item.phase === phase).sort((a, b) => a.position - b.position);
    if (items.length === 0) continue;
    phases.push({
      label: PLAN_PHASE_LABELS[phase],
      minutes: plan.minutesByPhase[phase],
      items: items.map((item, index) => {
        const id = itemExerciseId(item);
        const detail = id ? exercises[id] : undefined;
        const diagram = item.exercise?.diagram ?? detail?.diagram ?? null;
        return {
          number: index + 1,
          title: item.title,
          minutes: item.durationMinutes,
          sets: item.exercise ? formatSets(item.exercise) : null,
          note: item.note,
          coaching: detail && !detail.isLink ? coachingPoints(detail.coaching) : [],
          picture: diagram ? options.renderSketch(diagram, item.title) : null,
        };
      }),
    });
  }
  return { title: options.title ?? plan.title, subtitle: options.subtitle, focus: plan.focus, totalMinutes: plan.totalMinutes, brand: options.brand, phases };
}

export const SESSION_IMAGE_WIDTH = 1080;
const S_PAD = 48;
const S_SKETCH = 300;

/**
 * Eine ganze Einheit als hohes PNG, 1080 Pixel breit (gut fuer Messenger): Titel, Termin, je Phase die
 * Uebungen mit Minuten, kleiner Skizze und den wichtigsten Coaching-Punkten.
 */
export async function makeSessionImage(data: SessionImageData): Promise<Blob> {
  const images = new Map<SvgPicture, HTMLImageElement>();
  await Promise.all(
    data.phases.flatMap((phase) => phase.items).flatMap((item) => (item.picture ? [item.picture] : []))
      .map(async (picture) => { images.set(picture, await loadSvgImage(picture.svg)); }),
  );

  const contentWidth = SESSION_IMAGE_WIDTH - 2 * S_PAD;
  const measure = measuring();
  const draws: ((ctx: Context) => void)[] = [];
  const font = (weight: number, size: number): string => `${weight} ${size}px ${IMAGE_FONT}`;
  const lines = (text: string, weight: number, size: number, width: number, max: number): string[] => {
    measure.font = font(weight, size);
    return wrapLines(measure, text, width, max);
  };
  const text = (value: string, x: number, y: number, weight: number, size: number, color: string, align: CanvasTextAlign = 'left'): void => {
    draws.push((ctx) => {
      ctx.font = font(weight, size);
      ctx.fillStyle = color;
      ctx.textAlign = align;
      ctx.textBaseline = 'top';
      ctx.fillText(value, x, y);
    });
  };

  let y = S_PAD;
  text(data.brand, S_PAD, y, 600, 26, '#94a3b8');
  y += 46;
  for (const line of lines(data.title, 700, 54, contentWidth, 2)) {
    text(line, S_PAD, y, 700, 54, '#0f172a');
    y += 66;
  }
  y += 4;
  for (const line of lines(data.subtitle, 400, 30, contentWidth, 2)) {
    text(line, S_PAD, y, 400, 30, '#475569');
    y += 40;
  }
  if (data.focus.trim()) {
    y += 6;
    for (const line of lines(`Schwerpunkt: ${data.focus}`, 600, 30, contentWidth, 2)) {
      text(line, S_PAD, y, 600, 30, '#1e293b');
      y += 40;
    }
  }
  text(`Gesamt: ${data.totalMinutes} Min.`, S_PAD, y + 6, 600, 30, '#1e293b');
  y += 60;

  for (const phase of data.phases) {
    const top = y;
    draws.push((ctx) => {
      ctx.fillStyle = '#1b5df5';
      roundedBox(ctx, S_PAD, top, contentWidth, 56, 14);
      ctx.fill();
    });
    text(phase.label, S_PAD + 20, top + 11, 700, 32, '#ffffff');
    text(`${phase.minutes} Min.`, S_PAD + contentWidth - 20, top + 11, 700, 32, '#ffffff', 'right');
    y += 56 + 16;

    for (const item of phase.items) {
      const image = item.picture ? images.get(item.picture) : undefined;
      const sketchWidth = item.picture ? S_SKETCH : 0;
      const sketchHeight = item.picture ? (S_SKETCH * item.picture.height) / item.picture.width : 0;
      const inner = 20;
      const textX = S_PAD + inner + (sketchWidth ? sketchWidth + 24 : 0);
      const textWidth = SESSION_IMAGE_WIDTH - S_PAD - inner - textX;

      const heading = lines(`${item.number}. ${item.title}`, 700, 32, textWidth - 130, 2);
      const setsLines = item.sets ? lines(item.sets, 600, 26, textWidth, 2) : [];
      const noteLines = item.note ? lines(item.note, 400, 26, textWidth, 2) : [];
      const points = item.coaching.map((point) => lines(point, 400, 26, textWidth - 24, 2));

      const blockTop = y;
      let ty = blockTop + inner;
      heading.forEach((line, index) => {
        text(line, textX, ty, 700, 32, '#0f172a');
        if (index === 0) text(`${item.minutes} Min.`, SESSION_IMAGE_WIDTH - S_PAD - inner, ty, 600, 30, '#475569', 'right');
        ty += 40;
      });
      for (const line of setsLines) {
        text(line, textX, ty, 600, 26, '#334155');
        ty += 34;
      }
      for (const line of noteLines) {
        text(line, textX, ty, 400, 26, '#64748b');
        ty += 34;
      }
      if (points.length > 0) {
        ty += 6;
        text('Coaching', textX, ty, 700, 26, '#1b5df5');
        ty += 34;
        for (const point of points) {
          point.forEach((line, index) => {
            if (index === 0) text('•', textX, ty, 400, 26, '#334155');
            text(line, textX + 24, ty, 400, 26, '#334155');
            ty += 34;
          });
        }
      }
      const blockHeight = Math.max(ty - blockTop, sketchHeight + inner) + inner;
      draws.push((ctx) => {
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 2;
        roundedBox(ctx, S_PAD, blockTop, contentWidth, blockHeight, 16);
        ctx.stroke();
        if (image) ctx.drawImage(image, S_PAD + inner, blockTop + inner, sketchWidth, sketchHeight);
      });
      y = blockTop + blockHeight + 14;
    }
    y += 12;
  }

  text(data.brand, SESSION_IMAGE_WIDTH / 2, y + 6, 500, 24, '#94a3b8', 'center');
  const height = y + 6 + 24 + S_PAD;

  const { canvas, ctx } = newCanvas(SESSION_IMAGE_WIDTH, height);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SESSION_IMAGE_WIDTH, height);
  for (const draw of draws) draw(ctx);
  return canvasToBlob(canvas);
}

/* -------------------------------------------------------------------- Teilen */

export type ShareOutcome = 'shared' | 'downloaded' | 'cancelled';

/**
 * Am Handy ueber das Teilen-Fenster des Systems (WhatsApp usw.), sonst als Datei herunterladen.
 * `navigator.canShare({ files })` entscheidet; bricht jemand das Teilen ab, ist das keine Stoerung.
 */
export async function shareOrDownload(blob: Blob, fileName: string, title: string): Promise<ShareOutcome> {
  const file = new File([blob], fileName, { type: 'image/png' });
  if (typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title });
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
      // Ein anderer Fehler (etwa abgelaufene Geste am iPhone): dann wenigstens herunterladen.
    }
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}
